package uz.mint.frontend.components.sections

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import uz.mint.frontend.PageGutter
import uz.mint.frontend.ShellStyle
import uz.mint.frontend.SiteConfig
import uz.mint.frontend.api.AuthApi
import uz.mint.frontend.api.SessionState
import uz.mint.frontend.api.SessionStore
import uz.mint.frontend.api.rememberSession
import uz.mint.frontend.flexWrapCss
import uz.mint.frontend.i18n.AccountStrings
import uz.mint.frontend.i18n.Locale
import uz.mint.frontend.i18n.accountStringsFor
import uz.mint.frontend.i18n.stringsFor
import uz.mint.frontend.toSitePalette

/**
 * Sign-in and registration, which are the same thing.
 *
 * Two steps on one route rather than two: the code step needs the number from the first step,
 * and putting it in the URL would both leak the number into history and let someone land on a
 * code form with no challenge behind it.
 *
 * @param redirectTo where to send the customer once they are in. Defaults to their eSIMs,
 *   which is what someone who just signed in almost always wanted.
 */
@Composable
fun LoginPage(locale: Locale, defaultRedirect: String = locale.esimsPath) {
    val strings = stringsFor(locale)
    val account = accountStringsFor(locale)
    val redirectTo = remember(defaultRedirect) { safeNext() ?: defaultRedirect }
    val palette = ColorMode.current.toSitePalette()
    val session = rememberSession()
    val scope = rememberCoroutineScope()

    var phone by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var maskedPhone by remember { mutableStateOf("") }
    var step by remember { mutableStateOf(Step.PHONE) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var resendIn by remember { mutableStateOf(0) }
    var legal by remember { mutableStateOf<LegalDoc?>(null) }

    // Ticks the resend cooldown down to zero. Restarted whenever a new code is sent.
    LaunchedEffect(resendIn) {
        if (resendIn > 0) {
            delay(1000)
            resendIn -= 1
        }
    }

    // Someone who is already signed in has no business on this form.
    LaunchedEffect(session) {
        if (session is SessionState.SignedIn) window.location.replace(redirectTo)
    }

    fun sendCode() {
        if (busy) return
        busy = true
        error = null
        scope.launch {
            when (val result = AuthApi.requestCode(phone, locale)) {
                is AuthApi.RequestCodeResult.Sent -> {
                    maskedPhone = result.phoneMasked
                    resendIn = result.resendAfterSeconds
                    step = Step.CODE
                    code = ""
                }
                is AuthApi.RequestCodeResult.TooSoon -> {
                    resendIn = result.retryAfterSeconds
                    error = account.withCount(account.errTooSoon, result.retryAfterSeconds)
                    // Still advance: a code was sent a moment ago and is what they should type.
                    if (step == Step.PHONE) step = Step.CODE
                }
                AuthApi.RequestCodeResult.InvalidPhone -> error = account.errInvalidPhone
                AuthApi.RequestCodeResult.DeliveryFailed -> error = account.errDeliveryFailed
                is AuthApi.RequestCodeResult.Failed -> error = account.errGeneric
            }
            busy = false
        }
    }

    fun verify() {
        if (busy || code.isBlank()) return
        busy = true
        error = null
        scope.launch {
            when (val result = AuthApi.verifyCode(phone, code, locale)) {
                is AuthApi.VerifyResult.Success -> {
                    SessionStore.signedIn(result.user)
                    window.location.replace(redirectTo)
                }
                is AuthApi.VerifyResult.WrongCode ->
                    error = account.withCount(account.errWrongCode, result.attemptsLeft)
                AuthApi.VerifyResult.Expired -> {
                    error = account.errExpired
                    // The challenge is dead, so let them ask for a new one immediately.
                    resendIn = 0
                }
                AuthApi.VerifyResult.Blocked -> error = account.errBlocked
                is AuthApi.VerifyResult.Failed -> error = account.errGeneric
            }
            busy = false
        }
    }

    Column(Modifier.fillMaxWidth()) {
        NavHeader(
            locale = locale,
            strings = strings,
            anchorBase = locale.homePath,
            localePath = { it.loginPath },
        )

        Div(
            ShellStyle.toModifier()
                .padding(top = clamp(36.px, 4.vw, 72.px), bottom = clamp(48.px, 5.vw, 96.px), leftRight = PageGutter)
                .toAttrs()
        ) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .maxWidth(460.px)
                    .border(2.px, LineStyle.Solid, palette.rule)
                    .backgroundColor(palette.card)
                    .padding(clamp(24.px, 3.vw, 34.px))
                    .gap(18.px)
            ) {
                SpanText(
                    if (step == Step.PHONE) account.loginTitle else account.codeTitle,
                    Modifier.fontSize(26.px).fontWeight(FontWeight.ExtraBold).letterSpacing((-0.03).em),
                )
                SpanText(
                    if (step == Step.PHONE) account.loginLede
                    else account.withPhone(account.codeLede, maskedPhone),
                    Modifier.fontSize(15.px).color(palette.ink2).lineHeight(1.5),
                )

                when (step) {
                    Step.PHONE -> {
                        FieldLabel(account.loginPhoneLabel)
                        Row(
                            Modifier.fillMaxWidth().gap(0.px),
                            verticalAlignment = com.varabyte.kobweb.compose.ui.Alignment.CenterVertically,
                        ) {
                            // The country code is fixed rather than typed: every customer is on
                            // an Uzbek number, and it is one less thing to get wrong.
                            SpanText(
                                "+998",
                                Modifier
                                    .border(2.px, LineStyle.Solid, palette.rule)
                                    .borderRight(0.px, LineStyle.None, palette.rule)
                                    .padding(topBottom = 14.px, leftRight = 14.px)
                                    .fontSize(16.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(palette.ink2)
                                    .whiteSpace(WhiteSpace.NoWrap),
                            )
                            TextField(
                                value = phone,
                                placeholder = account.loginPhonePlaceholder,
                                type = InputType.Tel,
                                autoFocus = true,
                                onValue = { phone = it },
                                onEnter = ::sendCode,
                            )
                        }

                        PrimaryAction(
                            label = if (busy) account.loginSending else account.loginSend,
                            enabled = !busy && phone.count(Char::isDigit) >= MIN_DIGITS,
                            onClick = ::sendCode,
                        )

                        SpanText(
                            account.loginNote,
                            Modifier.fontSize(13.px).color(palette.ink2).lineHeight(1.5),
                        )
                    }

                    Step.CODE -> {
                        FieldLabel(account.codeLabel)
                        TextField(
                            value = code,
                            placeholder = account.codePlaceholder,
                            type = InputType.Text,
                            autoFocus = true,
                            // Brings up the numeric keypad and lets the OS autofill the code
                            // straight from the SMS.
                            inputMode = "numeric",
                            autoComplete = "one-time-code",
                            onValue = { code = it.filter(Char::isDigit).take(CODE_LENGTH) },
                            onEnter = ::verify,
                        )

                        PrimaryAction(
                            label = if (busy) account.codeVerifying else account.codeVerify,
                            enabled = !busy && code.length >= 4,
                            onClick = ::verify,
                        )

                        Row(Modifier.fillMaxWidth().gap(16.px).flexWrapCss()) {
                            if (resendIn > 0) {
                                SpanText(
                                    account.withCount(account.codeResendIn, resendIn),
                                    Modifier.fontSize(14.px).color(palette.ink2),
                                )
                            } else {
                                TextAction(account.codeResend, palette.ink) { sendCode() }
                            }
                            TextAction(account.codeChangePhone, palette.ink2) {
                                step = Step.PHONE
                                code = ""
                                error = null
                            }
                        }
                    }
                }

                error?.let {
                    SpanText(
                        it,
                        Modifier
                            .fillMaxWidth()
                            .border(2.px, LineStyle.Solid, palette.rule)
                            .padding(topBottom = 12.px, leftRight = 14.px)
                            .fontSize(14.px)
                            .fontWeight(FontWeight.SemiBold)
                            .lineHeight(1.45),
                    )
                }

                // The way out when SMS will not arrive at all — the same fallback the
                // checkout dialog offers.
                Link(
                    path = SiteConfig.TELEGRAM_URL,
                    modifier = Modifier.fontSize(13.px).color(palette.ink2),
                    variant = UndecoratedLinkVariant,
                ) { SpanText("Telegram") }
            }
        }

        Footer(
            locale = locale,
            strings = strings,
        )
    }

    when (legal) {
        LegalDoc.OFFER -> LegalOverlay(strings.legalOffer, uz.mint.frontend.i18n.PublicOffer, strings) { legal = null }
        LegalDoc.PRIVACY -> LegalOverlay(strings.legalPrivacy, uz.mint.frontend.i18n.PrivacyPolicy, strings) { legal = null }
        null -> Unit
    }
}

private enum class Step { PHONE, CODE }

/**
 * Where to go after signing in, taken from `?next=`.
 *
 * **Only site-relative paths are honoured.** A `next` that could name another origin turns the
 * login page into an open redirect — the classic phishing hop, where a link that genuinely
 * starts at mint.uz lands somewhere else. `//evil.example` is rejected alongside absolute URLs
 * because browsers read it as protocol-relative and would leave the site.
 */
private fun safeNext(): String? {
    val raw = URLSearchParams(window.location.search).get("next") ?: return null
    return raw.takeIf { it.startsWith("/") && !it.startsWith("//") }
}

private external class URLSearchParams(init: String) {
    fun get(name: String): String?
}

@Composable
private fun FieldLabel(text: String) {
    val palette = ColorMode.current.toSitePalette()
    SpanText(
        text,
        Modifier.fontSize(12.px).fontWeight(FontWeight.Bold).letterSpacing(0.08.em)
            .textTransform(TextTransform.Uppercase).color(palette.ink2),
    )
}

/** Shared input styling; the design has no form controls, so these follow its 2px rule. */
@Composable
internal fun TextField(
    value: String,
    placeholder: String,
    type: InputType<String>,
    autoFocus: Boolean = false,
    inputMode: String? = null,
    autoComplete: String? = null,
    onValue: (String) -> Unit,
    onEnter: (() -> Unit)? = null,
) {
    val palette = ColorMode.current.toSitePalette()
    Input(
        type = type,
        attrs = Modifier
            .fillMaxWidth()
            .border(2.px, LineStyle.Solid, palette.rule)
            .backgroundColor(palette.paper)
            .color(palette.ink)
            .padding(topBottom = 14.px, leftRight = 14.px)
            .fontSize(16.px)
            .fontFamily("inherit")
            .toAttrs {
                value(value)
                attr("placeholder", placeholder)
                if (autoFocus) attr("autofocus", "")
                inputMode?.let { attr("inputmode", it) }
                autoComplete?.let { attr("autocomplete", it) }
                onInput { onValue(it.value) }
                onKeyDown { event -> if (event.key == "Enter") onEnter?.invoke() }
            }
    )
}

@Composable
private fun PrimaryAction(label: String, enabled: Boolean, onClick: () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Button(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 15.px)
            .fontSize(16.px)
            .fontWeight(FontWeight.Bold)
            .fontFamily("inherit")
            // Disabled is a pale surface with a rule around it, not a fill of `rule`.
            //
            // `rule` is a line colour, and in the light palette it is #0F2620 — near-black. Filling
            // with it put dark-green `ink2` text on a near-black slab at a contrast of 1.29: the
            // button that says «Получить код» was legible only in the dark theme, and this is the
            // first screen a customer who cannot log in is looking at. The dark theme hid it
            // because there `rule` is a mid-tone and `ink2` is pale.
            .border(if (enabled) 0.px else 2.px, if (enabled) LineStyle.None else LineStyle.Solid, palette.rule)
            .backgroundColor(if (enabled) palette.solidBg else palette.paper2)
            .color(if (enabled) palette.solidFg else palette.ink2)
            .opacity(if (enabled) 1.0 else 0.7)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .onClick { if (enabled) onClick() }
            .toAttrs { if (!enabled) attr("disabled", "") }
    ) { Text(label) }
}

/** A `<button>` styled as a link — these act on the page, they do not navigate. */
@Composable
internal fun TextAction(
    label: String,
    color: com.varabyte.kobweb.compose.ui.graphics.Color,
    onClick: () -> Unit,
) {
    Button(
        attrs = Modifier
            .backgroundColor(Colors.Transparent)
            .border(0.px, LineStyle.None, color)
            .padding(0.px)
            .color(color)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .fontFamily("inherit")
            .textDecorationLine(TextDecorationLine.Underline)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .toAttrs()
    ) { Text(label) }
}

/** Nine subscriber digits; the +998 prefix is fixed by the field beside the input. */
private const val MIN_DIGITS = 9
private const val CODE_LENGTH = 6
